import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Camera, Loader2, Save, User as UserIcon, Check, Trash2, Upload, Fingerprint, Plus } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { VapeAvatar, UserAvatar } from '../components/VapeAvatars';
import AVATARS from '../components/VapeAvatars';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MapPin, Map as MapIcon, ChevronDown, ChevronUp, Star, Lock, Eye, EyeOff, ShieldCheck, Bell } from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import MyReviewsSection from '../components/MyReviewsSection';
import AddressMapPicker from '../components/AddressMapPicker';
import PushNotifications from '../components/PushNotifications';
import { isPasskeySupported, startPasskeyRegistration } from '../lib/passkey';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'datos';
  const { user, loading: authLoading, refresh } = useAuth();
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) navigate('/inicio-sesion');
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setLastName(user.last_name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  if (authLoading || !user) return (
    <div className="max-w-3xl mx-auto px-4 mt-20 text-white/70">
      <Loader2 className="w-6 h-6 animate-spin inline" /> Cargando...
    </div>
  );

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.patch('/auth/me', { name, last_name: lastName });
      await refresh();
      toast({ title: 'Perfil actualizado' });
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'No se guardó' }); }
    finally { setSaving(false); }
  };

  const pickAvatar = async (id) => {
    try {
      await api.patch('/auth/me/picture', { picture: `avatar:${id}` });
      await refresh();
      toast({ title: 'Avatar actualizado' });
    } catch (e) { toast({ title: 'Error', description: 'No se pudo actualizar' }); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 300 * 1024) {
      toast({ title: 'Imagen muy grande', description: 'Máximo 300 KB' });
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.patch('/auth/me/picture', { picture: reader.result });
        await refresh();
        toast({ title: 'Foto actualizada' });
      } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo subir' }); }
    };
    reader.readAsDataURL(file);
  };

  const removePicture = async () => {
    try {
      await api.patch('/auth/me/picture', { picture: '' });
      await refresh();
      toast({ title: 'Foto eliminada' });
    } catch {}
  };

  const currentAvatarId = user.picture?.startsWith('avatar:') ? user.picture.replace('avatar:', '') : null;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 mt-6 pb-32">
      <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white text-sm inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>

      <h1 className="text-3xl md:text-4xl font-black text-white">Mi perfil</h1>
      <p className="text-white/50 mt-1">Personaliza tus datos, avatar y dirección.</p>

      <Tabs defaultValue={initialTab} className="mt-6">
        <div className="-mx-4 md:mx-0 px-4 md:px-0 overflow-x-auto no-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>
          <TabsList className="bg-neutral-950 border border-white/10 w-max min-w-full md:w-full flex flex-row h-12 md:h-13 p-1 px-2 md:px-2 gap-1.5 md:gap-1.5 items-center">
            <TabsTrigger value="datos" className="shrink-0 md:flex-auto w-auto data-[state=active]:bg-orange-500 data-[state=active]:text-black text-white/70 px-3 py-2 h-10 md:h-11 text-xs md:text-sm font-bold flex items-center justify-center gap-1.5 rounded-lg relative data-[state=active]:z-10"><UserIcon className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" /> Datos</TabsTrigger>
            <TabsTrigger value="direccion" className="shrink-0 md:flex-auto w-auto data-[state=active]:bg-orange-500 data-[state=active]:text-black text-white/70 px-3 py-2 h-10 md:h-11 text-xs md:text-sm font-bold flex items-center justify-center gap-1.5 rounded-lg relative data-[state=active]:z-10"><MapPin className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" /> Dirección</TabsTrigger>
            <TabsTrigger value="avatar" className="shrink-0 md:flex-auto w-auto data-[state=active]:bg-orange-500 data-[state=active]:text-black text-white/70 px-3 py-2 h-10 md:h-11 text-xs md:text-sm font-bold flex items-center justify-center gap-1.5 rounded-lg relative data-[state=active]:z-10"><Camera className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" /> Avatar</TabsTrigger>
            <TabsTrigger value="seguridad" data-testid="security-tab" className="shrink-0 md:flex-auto w-auto data-[state=active]:bg-orange-500 data-[state=active]:text-black text-white/70 px-3 py-2 h-10 md:h-11 text-xs md:text-sm font-bold flex items-center justify-center gap-1.5 rounded-lg relative data-[state=active]:z-10"><Lock className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" /> Seguridad</TabsTrigger>
            <TabsTrigger value="notificaciones" data-testid="notifications-tab" className="shrink-0 md:flex-auto w-auto data-[state=active]:bg-orange-500 data-[state=active]:text-black text-white/70 px-3 py-2 h-10 md:h-11 text-xs md:text-sm font-bold flex items-center justify-center gap-1.5 rounded-lg relative overflow-visible data-[state=active]:z-10">
              <Bell className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" />
              <span>Notificaciones</span>
              <span className="absolute top-1 right-2 flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
              </span>
            </TabsTrigger>
            <TabsTrigger value="resenas" data-testid="reviews-tab" className="shrink-0 md:flex-auto w-auto data-[state=active]:bg-orange-500 data-[state=active]:text-black text-white/70 px-3 py-2 h-10 md:h-11 text-xs md:text-sm font-bold flex items-center justify-center gap-1.5 rounded-lg relative data-[state=active]:z-10"><Star className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" /> Reseñas</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="avatar" className="mt-4">
      <section className="mt-6 bg-neutral-950 border border-white/10 rounded-2xl p-5">
        <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Camera className="w-4 h-4 text-orange-400" /> Foto de perfil</h3>
        <div className="flex items-center gap-5">
          <UserAvatar picture={user.picture} name={user.name} phone={user.phone} email={user.email} size={96} />
          <div className="flex-1 flex flex-wrap gap-2">
            <Button onClick={() => fileRef.current?.click()} className="bg-white text-black hover:bg-white/90 font-semibold">
              <Upload className="w-4 h-4 mr-1" /> Subir foto
            </Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            {user.picture && (
              <Button onClick={removePicture} variant="outline" className="bg-transparent border-white/20 text-white hover:bg-rose-500/10 hover:text-rose-300 hover:border-rose-500/30">
                <Trash2 className="w-4 h-4 mr-1" /> Quitar
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6">
          <p className="text-white/70 text-sm font-semibold mb-3">O elige un avatar vape</p>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
            {AVATARS.map((a) => (
              <button
                key={a.id}
                onClick={() => pickAvatar(a.id)}
                className={`relative aspect-square rounded-2xl border-2 transition-all p-1 ${
                  currentAvatarId === a.id ? 'border-orange-500 shadow-lg shadow-orange-500/30 scale-105' : 'border-white/10 hover:border-orange-500/50'
                }`}
                title={a.name}
              >
                <VapeAvatar id={a.id} size={72} />
                {currentAvatarId === a.id && (
                  <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-orange-500 text-black flex items-center justify-center">
                    <Check className="w-3 h-3" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      </TabsContent>

      <TabsContent value="datos" className="mt-4">
        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2"><UserIcon className="w-4 h-4 text-orange-400" /> Datos personales</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/60">Nombre</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="text-xs text-white/60">Apellido</label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-white/60">Correo electrónico</label>
              <Input value={email} disabled className="bg-white/5 border-white/10 text-white/60 cursor-not-allowed" data-testid="profile-email-readonly" />
              <p className="text-[10px] text-white/40 mt-1">Para cambiar tu correo, contacta a soporte.</p>
            </div>
          </div>
          <Button onClick={saveProfile} disabled={saving} className="mt-5 bg-orange-500 hover:bg-orange-600 text-black font-bold">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Guardando...</> : <><Save className="w-4 h-4 mr-1" /> Guardar cambios</>}
          </Button>
        </section>
      </TabsContent>

      <TabsContent value="direccion" className="mt-4">
        <MyAddressSection user={user} refresh={refresh} />
      </TabsContent>

      <TabsContent value="seguridad" className="mt-4 space-y-4">
        <ChangePasswordSection user={user} />
        <PasskeysSection />
      </TabsContent>

      <TabsContent value="notificaciones" className="mt-4">
        <PushNotifications />
      </TabsContent>

      <TabsContent value="resenas" className="mt-4">
        <MyReviewsSection />
      </TabsContent>
    </Tabs>
    </div>
  );
};

const MyAddressSection = ({ user, refresh }) => {
  const initial = user.address || {};
  const [editing, setEditing] = useState(!initial.calle && !initial.full);
  const [showMap, setShowMap] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    alias: initial.alias || 'Casa',
    calle: initial.calle || '',
    ext: initial.ext || '',
    interior: initial.interior || '',
    colonia: initial.colonia || '',
    cp: initial.cp || initial.zip || user.coverage_zip || '',
    ciudad: initial.ciudad || '',
    estado: initial.estado || '',
    entre_calle: initial.entre_calle || '',
    y_calle: initial.y_calle || '',
    referencia: initial.referencia || '',
    lat: initial.lat || null,
    lng: initial.lng || null,
    full: initial.full || '',
  });

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onMapPick = (v) => {
    const d = v.details || {};
    setForm((f) => ({
      ...f,
      lat: v.lat,
      lng: v.lng,
      full: v.address || f.full,
      cp: d.postcode || v.zip || f.cp,
      calle: d.road || f.calle,
      colonia: d.neighbourhood || d.suburb || f.colonia,
      ciudad: d.city || d.town || d.village || f.ciudad,
      estado: d.state || f.estado,
    }));
  };

  const save = async () => {
    if (!form.calle || !form.cp) { toast({ title: 'Faltan datos', description: 'Calle y CP son obligatorios' }); return; }
    setSaving(true);
    try {
      const full = form.full || `${form.calle} ${form.ext}${form.interior ? ' int ' + form.interior : ''}, ${form.colonia}, CP ${form.cp}${form.ciudad ? ', ' + form.ciudad : ''}${form.estado ? ', ' + form.estado : ''}`.trim();
      await api.patch('/auth/me', { address: { ...form, full } });
      toast({ title: '¡Dirección guardada!', description: 'Se autollenará al comprar.' });
      setEditing(false);
      refresh?.();
    } catch (err) {
      toast({ title: 'No se pudo guardar', description: err?.response?.data?.detail || 'Intenta de nuevo.' });
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!confirm('¿Eliminar tu dirección guardada?')) return;
    setSaving(true);
    try {
      await api.patch('/auth/me', { address: {} });
      toast({ title: 'Dirección eliminada' });
      setEditing(true);
      setForm({ alias: 'Casa', calle: '', ext: '', interior: '', colonia: '', cp: '', ciudad: '', estado: '', entre_calle: '', y_calle: '', referencia: '', lat: null, lng: null, full: '' });
      refresh?.();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Intenta de nuevo.' });
    } finally { setSaving(false); }
  };

  const hasAddress = !!(initial.calle || initial.full);

  // Read-only view
  if (!editing && hasAddress) {
    const fields = [
      { label: 'Alias', v: initial.alias },
      { label: 'CP', v: initial.cp || initial.zip || user.coverage_zip },
      { label: 'Colonia', v: initial.colonia },
      { label: 'Calle', v: initial.calle },
      { label: 'No. Exterior', v: initial.ext },
      { label: 'No. Interior', v: initial.interior },
      { label: 'Entre calle', v: initial.entre_calle },
      { label: 'Y calle', v: initial.y_calle },
      { label: 'Referencia', v: initial.referencia },
    ].filter((f) => f.v);
    return (
      <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5 space-y-3" data-testid="address-view">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-white font-bold flex items-center gap-2"><MapPin className="w-4 h-4 text-orange-400" /> Dirección de envío</h3>
          <div className="flex items-center gap-2">
            <Button onClick={() => setEditing(true)} variant="outline" className="h-9 rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="address-edit-btn">Editar</Button>
            <Button onClick={del} variant="outline" className="h-9 rounded-full border-red-500/25 text-red-300 hover:bg-red-500/10">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
            </Button>
          </div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/25 p-4 flex items-start gap-3">
          <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-white text-sm">Al finalizar tu pedido usaremos esta dirección <strong>automáticamente</strong>.</p>
            {(initial.calle || initial.full) && (
              <p className="text-white/70 text-sm mt-1">
                {initial.calle
                  ? [
                      `${initial.calle} ${initial.ext || ''}${initial.interior ? ' int ' + initial.interior : ''}`.trim(),
                      initial.colonia,
                      initial.cp ? `CP ${initial.cp}` : null,
                      initial.ciudad,
                      initial.estado,
                    ].filter(Boolean).join(', ')
                  : initial.full}
              </p>
            )}
          </div>
        </div>
        {fields.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {fields.map((f) => (
              <div key={f.label} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                <p className="text-white/50 text-[10px] uppercase tracking-wide">{f.label}</p>
                <p className="text-white text-sm font-semibold truncate">{f.v}</p>
              </div>
            ))}
          </div>
        )}
        {initial.lat && initial.lng && (
          <>
            <button onClick={() => setShowMap((v) => !v)} className="w-full h-11 rounded-xl bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-300 font-semibold text-sm inline-flex items-center justify-center gap-2 transition">
              <MapIcon className="w-4 h-4" /> {showMap ? 'Ocultar mapa' : 'Ver en el mapa'}
              {showMap ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showMap && (
              <div className="rounded-xl overflow-hidden border border-white/10" style={{ height: 280 }}>
                <MapContainer center={[initial.lat, initial.lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
                  <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                  <Marker position={[initial.lat, initial.lng]} />
                </MapContainer>
              </div>
            )}
          </>
        )}
      </section>
    );
  }

  // Editing view (create or edit)
  return (
    <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5 space-y-4" data-testid="address-edit">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-white font-bold flex items-center gap-2">
          <MapPin className="w-4 h-4 text-orange-400" /> {hasAddress ? 'Editar dirección' : 'Registra tu dirección de envío'}
        </h3>
        {hasAddress && (
          <button onClick={() => setEditing(false)} className="text-xs text-white/50 hover:text-white">Cancelar</button>
        )}
      </div>
      <p className="text-white/50 text-xs">Guarda tu dirección una sola vez. Se autocompletará en cada pedido para ahorrar tiempo.</p>

      <div className="rounded-xl overflow-hidden border border-white/10" style={{ height: 320 }}>
        <AddressMapPicker
          value={{ lat: form.lat, lng: form.lng, address: form.full, zip: form.cp }}
          onChange={onMapPick}
          height={320}
          showZones
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Alias</label>
          <Input value={form.alias} onChange={(e) => patch('alias', e.target.value)} placeholder="Casa / Oficina" className="h-11 mt-2 bg-white/5 border-white/10 text-white" data-testid="addr-alias" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Calle <span className="text-orange-400">*</span></label>
          <Input value={form.calle} onChange={(e) => patch('calle', e.target.value)} placeholder="Ej. Av. Constitución" required className="h-11 mt-2 bg-white/5 border-white/10 text-white" data-testid="addr-street" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">No. Ext <span className="text-orange-400">*</span></label>
          <Input value={form.ext} onChange={(e) => patch('ext', e.target.value)} placeholder="123" required className="h-11 mt-2 bg-white/5 border-white/10 text-white" data-testid="addr-ext" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">No. Int</label>
          <Input value={form.interior} onChange={(e) => patch('interior', e.target.value)} placeholder="Depto/Loc" className="h-11 mt-2 bg-white/5 border-white/10 text-white" data-testid="addr-int" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">CP <span className="text-orange-400">*</span></label>
          <Input value={form.cp} onChange={(e) => patch('cp', e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="64000" required className="h-11 mt-2 bg-white/5 border-white/10 text-white" data-testid="addr-cp" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Colonia <span className="text-orange-400">*</span></label>
          <Input value={form.colonia} onChange={(e) => patch('colonia', e.target.value)} placeholder="Colonia o fraccionamiento" required className="h-11 mt-2 bg-white/5 border-white/10 text-white" data-testid="addr-colonia" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Ciudad <span className="text-orange-400">*</span></label>
          <Input value={form.ciudad} onChange={(e) => patch('ciudad', e.target.value)} placeholder="Monterrey" required className="h-11 mt-2 bg-white/5 border-white/10 text-white" data-testid="addr-city" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Estado <span className="text-orange-400">*</span></label>
          <Input value={form.estado} onChange={(e) => patch('estado', e.target.value)} placeholder="Nuevo León" required className="h-11 mt-2 bg-white/5 border-white/10 text-white" data-testid="addr-state" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Entre calle</label>
          <Input value={form.entre_calle} onChange={(e) => patch('entre_calle', e.target.value)} placeholder="Opcional" className="h-11 mt-2 bg-white/5 border-white/10 text-white" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Y calle</label>
          <Input value={form.y_calle} onChange={(e) => patch('y_calle', e.target.value)} placeholder="Opcional" className="h-11 mt-2 bg-white/5 border-white/10 text-white" />
        </div>
        <div className="md:col-span-3">
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Referencia para el repartidor</label>
          <Input value={form.referencia} onChange={(e) => patch('referencia', e.target.value)} placeholder="Ej. Portón negro, entre farmacia y OXXO" className="h-11 mt-2 bg-white/5 border-white/10 text-white" data-testid="addr-ref" />
        </div>
      </div>

      <Button onClick={save} disabled={saving || !form.calle || !form.ext || !form.cp || !form.colonia || !form.ciudad || !form.estado} className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-base shadow-lg shadow-orange-500/25" data-testid="addr-save-btn">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : <><Save className="w-4 h-4 mr-2" /> Guardar dirección</>}
      </Button>
    </section>
  );
};

function ChangePasswordSection({ user }) {
  const hasPassword = user?.auth_provider === 'email' || user?.has_password;
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { toast({ title: 'Las contraseñas no coinciden' }); return; }
    if (pw.length < 8) { toast({ title: 'Contraseña muy corta', description: 'Mínimo 8 caracteres' }); return; }
    setSaving(true);
    try {
      const res = await api.post('/auth/change-password', { current_password: current || undefined, new_password: pw });
      if (res?.data?.token) {
        try { localStorage.setItem('va_token', res.data.token); } catch {}
      }
      toast({ title: hasPassword ? 'Contraseña actualizada' : 'Contraseña creada', description: 'Cambio aplicado.' });
      setCurrent(''); setPw(''); setConfirm('');
    } catch (err) {
      toast({ title: 'No se pudo actualizar', description: err?.response?.data?.detail || 'Intenta de nuevo.' });
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5" data-testid="security-section">
      <div className="flex items-center gap-2 mb-1">
        <Lock className="w-4 h-4 text-orange-400" />
        <h3 className="text-white font-bold">{hasPassword ? 'Cambiar contraseña' : 'Crear contraseña'}</h3>
      </div>
      <p className="text-xs text-white/50 mb-4">
        {hasPassword
          ? 'Actualiza tu contraseña. Necesitarás tu contraseña actual.'
          : 'Ingresaste con Google — puedes crear una contraseña para acceder también con correo.'}
      </p>
      <form onSubmit={submit} className="space-y-4 max-w-md" autoComplete="off">
        {hasPassword && (
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Contraseña actual</label>
            <div className="relative mt-2">
              <Lock className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <Input type={showPw ? 'text' : 'password'} autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Tu contraseña actual" className="h-11 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50" data-testid="security-current-input" />
            </div>
          </div>
        )}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Nueva contraseña</label>
          <div className="relative mt-2">
            <Lock className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input type={showPw ? 'text' : 'password'} autoComplete="new-password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Mínimo 8 caracteres" className="h-11 pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50" data-testid="security-new-input" />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Confirmar</label>
          <div className="relative mt-2">
            <ShieldCheck className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input type={showPw ? 'text' : 'password'} autoComplete="new-password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repite la contraseña" className="h-11 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50" data-testid="security-confirm-input" />
          </div>
        </div>
        <Button type="submit" disabled={saving || !pw || !confirm} className="w-full h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold shadow-lg shadow-orange-500/25" data-testid="security-submit-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : (hasPassword ? 'Actualizar contraseña' : 'Crear contraseña')}
        </Button>
      </form>
    </section>
  );
}


function PasskeysSection() {
  const [keys, setKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoadingKeys(true);
    try {
      const { data } = await api.get('/auth/passkey/list');
      setKeys(data);
    } catch { setKeys([]); }
    finally { setLoadingKeys(false); }
  };

  useEffect(() => { load(); }, []);

  const addKey = async () => {
    setAdding(true);
    try {
      const device = navigator.userAgent.includes('iPhone') ? 'iPhone'
        : navigator.userAgent.includes('Android') ? 'Android'
        : `Dispositivo ${keys.length + 1}`;
      await startPasskeyRegistration(api, device);
      toast({ title: 'Llave guardada', description: 'Puedes ingresar con tu huella desde ahora.' });
      load();
    } catch (err) {
      if (err?.name !== 'NotAllowedError')
        toast({ title: 'No se pudo agregar', description: err?.message || 'Intenta de nuevo.' });
    } finally { setAdding(false); }
  };

  const deleteKey = async (credId) => {
    setDeletingId(credId);
    try {
      await api.delete(`/auth/passkey/${encodeURIComponent(credId)}`);
      toast({ title: 'Llave eliminada' });
      setKeys((k) => k.filter((c) => c.credential_id !== credId));
    } catch {
      toast({ title: 'Error al eliminar' });
    } finally { setDeletingId(null); }
  };

  if (!isPasskeySupported()) return null;

  return (
    <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5" data-testid="passkeys-section">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Fingerprint className="w-4 h-4 text-orange-400" />
          <h3 className="text-white font-bold">Llaves de acceso</h3>
        </div>
        <Button
          onClick={addKey}
          disabled={adding}
          size="sm"
          className="h-8 px-3 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-xs gap-1"
          data-testid="add-passkey-btn"
        >
          {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          Agregar
        </Button>
      </div>
      <p className="text-xs text-white/50 mb-4">Inicia sesión con tu huella dactilar, Face ID o PIN del dispositivo.</p>

      {loadingKeys ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-white/30" />
        </div>
      ) : keys.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
            <Fingerprint className="w-6 h-6 text-white/20" />
          </div>
          <p className="text-white/40 text-sm">Sin llaves guardadas</p>
          <p className="text-white/25 text-xs">Toca "Agregar" para guardar tu huella</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="passkey-list">
          {keys.map((k) => (
            <li key={k.credential_id} className="flex items-center justify-between gap-3 bg-white/5 rounded-xl px-4 py-3" data-testid={`passkey-item-${k.credential_id.slice(0,8)}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Fingerprint className="w-4 h-4 text-orange-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{k.name || 'Dispositivo'}</p>
                  <p className="text-white/40 text-xs">{k.created_at ? new Date(k.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</p>
                </div>
              </div>
              <button
                onClick={() => deleteKey(k.credential_id)}
                disabled={deletingId === k.credential_id}
                className="text-white/30 hover:text-red-400 transition-colors flex-shrink-0"
                data-testid={`delete-passkey-${k.credential_id.slice(0,8)}`}
                aria-label="Eliminar llave"
              >
                {deletingId === k.credential_id
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Trash2 className="w-4 h-4" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


export default ProfilePage;
